import React, { useEffect, useMemo, useState } from "react";
import { ArrowRight, Cpu, Smartphone } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "./ui/button";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "./ui/carousel";
import { openWhatsApp } from "../lib/whatsapp";

const TopAdSlider = () => {
  const publicUrl = process.env.PUBLIC_URL || "";
  const slides = useMemo(
    () => [
      {
        id: "combo",
        eyebrow: "Armado completo",
        title: "Tu PC gamer lista para jugar",
        description: "Elegimos cada componente, armamos, testeamos y dejamos todo optimizado.",
        badge: "PC Gamer",
        accent: "from-indigo-500 via-blue-500 to-teal-400",
        glow: "bg-blue-400/30",
        icon: ArrowRight,
        image1x: `${publicUrl}/images/ads/pc-gamer-1200.jpg`,
        image2x: `${publicUrl}/images/ads/pc-gamer-1800.jpg`,
        mobileImage: `${publicUrl}/images/ads/pc-gamer-900.jpg`
      },
      {
        id: "gpu",
        eyebrow: "Gaming & creación",
        title: "Tarjetas de video de última generación",
        description: "Potencia real para streaming, edición y juegos en ultra con armado experto.",
        badge: "GPU Power",
        accent: "from-sky-500 via-cyan-500 to-emerald-400",
        glow: "bg-cyan-400/30",
        icon: Cpu,
        image1x: `${publicUrl}/images/ads/gpu-1200.jpg`,
        image2x: `${publicUrl}/images/ads/gpu-1800.jpg`,
        mobileImage: `${publicUrl}/images/ads/gpu-900.jpg`
      },
      {
        id: "iphone",
        eyebrow: "Lanzamientos 2026",
        title: "Nuevos iPhone listos para brillar",
        description: "Stock actualizado, asesoría para elegir modelo y puesta a punto inmediata.",
        badge: "Apple Highlights",
        accent: "from-rose-500 via-pink-500 to-orange-400",
        glow: "bg-rose-400/30",
        icon: Smartphone,
        image1x: `${publicUrl}/images/ads/iphone-1200.jpg`,
        image2x: `${publicUrl}/images/ads/iphone-1800.jpg`,
        mobileImage: `${publicUrl}/images/ads/iphone-900.jpg`
      }
    ],
    [publicUrl]
  );

  const navigate = useNavigate();
  const [api, setApi] = useState(null);
  const [current, setCurrent] = useState(0);

  const handleStockClick = (slide) => {
    if (slide.id === "iphone") {
      const message =
        "Hola, quiero consultar stock de iPhone. ¿Qué modelos y capacidades tienen disponibles?";
      const encodedMessage = encodeURIComponent(message);
      window.open(`https://wa.me/2355514955?text=${encodedMessage}`, "_blank");
      return;
    }
    if (slide.id === "gpu") {
      openWhatsApp(
        "Hola, quiero consultar stock de placas de video. ¿Qué modelos tienen disponibles y qué me recomiendan según mi uso?"
      );
      return;
    }
    if (slide.id === "combo") {
      openWhatsApp(
        "Hola, quiero armar una PC gamer lista para jugar. ¿Me pasan opciones de combos y precios según presupuesto?"
      );
      return;
    }
    openWhatsApp(`Hola, quiero consultar stock sobre: ${slide.title}.`);
  };

  useEffect(() => {
    if (!api) {
      return undefined;
    }

    const onSelect = () => {
      setCurrent(api.selectedScrollSnap());
    };

    onSelect();
    api.on("select", onSelect);

    const interval = setInterval(() => {
      api.scrollNext();
    }, 6500);

    return () => {
      api.off("select", onSelect);
      clearInterval(interval);
    };
  }, [api]);

  const getNovedadesLink = (slideId) => {
    if (slideId === "iphone") {
      return "/tienda?categoria=iPhones#catalogo";
    }
    if (slideId === "gpu") {
      return "/tienda?categoria=Placas%20de%20Video#catalogo";
    }
    return "/tienda#catalogo";
  };

  return (
    <section className="relative w-full min-h-screen bg-slate-950 text-white overflow-hidden">
      <Carousel
        opts={{ loop: true }}
        setApi={setApi}
        className="relative"
        aria-label="Slider publicitario principal"
      >
        <CarouselContent className="ml-0">
          {slides.map((slide, index) => {
            const Icon = slide.icon;
            return (
              <CarouselItem key={slide.id} className="pl-0">
                <div className="relative h-[100svh] w-full flex items-center">
                  <picture className="absolute inset-0 h-full w-full">
                    <source media="(max-width: 768px)" srcSet={slide.mobileImage} />
                    <img
                      className="h-full w-full object-cover"
                      src={slide.image2x}
                      srcSet={`${slide.image1x} 1200w, ${slide.image2x} 1800w`}
                      sizes="100vw"
                      alt=""
                      loading={index === 0 ? "eager" : "lazy"}
                      decoding="async"
                      fetchPriority={index === 0 ? "high" : "auto"}
                      aria-hidden="true"
                    />
                  </picture>
                  <div className="absolute inset-0 bg-black/10" />
                  <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(255,255,255,0.15),_transparent_60%)]" />
                  <div className={`absolute -right-24 top-1/2 h-72 w-72 -translate-y-1/2 rounded-full blur-3xl ${slide.glow}`} />
                  <div className="absolute left-[-10%] bottom-[-20%] h-80 w-80 rounded-full bg-white/10 blur-3xl" />

                  <div className="relative z-10 w-full max-w-6xl mx-auto px-5 sm:px-10 pt-20 sm:pt-24">
                    <div className="relative flex flex-col gap-5 sm:gap-6 max-w-2xl text-white drop-shadow-[0_6px_18px_rgba(0,0,0,0.55)]">
                      <div className="absolute -inset-3 sm:-inset-6 bg-black/35 blur-2xl rounded-[32px] sm:rounded-[36px] -z-10" />
                      <div className="flex items-center gap-3">
                        <span className="text-[11px] tracking-[0.4em] uppercase text-white/80">
                          {slide.eyebrow}
                        </span>
                        <span className="text-xs font-semibold bg-white/20 px-3 py-1 rounded-full">
                          {slide.badge}
                        </span>
                      </div>

                      <h2 className="text-[2.15rem] sm:text-5xl lg:text-6xl font-semibold leading-tight">
                        {slide.title}
                      </h2>
                      <p className="text-base sm:text-xl text-white/90 leading-relaxed">
                        {slide.description}
                      </p>

                      <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                        <Button
                          className="h-11 sm:h-12 px-5 sm:px-6 text-base font-semibold bg-white text-slate-900 hover:bg-white/90"
                          onClick={() => navigate(getNovedadesLink(slide.id))}
                        >
                          Ver novedades
                        </Button>
                        <Button
                          variant="outline"
                          className="h-11 sm:h-12 px-5 sm:px-6 text-base font-semibold border-white/50 text-white bg-transparent hover:bg-white/10"
                          onClick={() => handleStockClick(slide)}
                        >
                          Consultar stock
                        </Button>
                      </div>

                      <div className="flex items-center gap-3 text-sm text-white/70">
                        <Icon className="h-5 w-5" />
                        <span>Asesoría personalizada en la tienda y por WhatsApp.</span>
                      </div>
                    </div>
                  </div>
                </div>
              </CarouselItem>
            );
          })}
        </CarouselContent>

        <CarouselPrevious className="hidden sm:flex -left-20 lg:-left-24" />
        <CarouselNext className="hidden sm:flex -right-20 lg:-right-24" />
      </Carousel>

      <div className="absolute bottom-5 sm:bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2">
        {slides.map((slide, index) => (
          <button
            key={slide.id}
            type="button"
            onClick={() => api?.scrollTo(index)}
            aria-label={`Ir al slide ${index + 1}`}
            className={`h-2 w-6 sm:w-8 rounded-full transition-all duration-300 ${
              current === index ? "bg-white" : "bg-white/40"
            }`}
          />
        ))}
      </div>
    </section>
  );
};

export default TopAdSlider;



